import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  Mic,
  MicOff,
  Phone,
  Share2,
  Shield,
  ShieldAlert,
  TriangleAlert,
  Users,
  X,
} from 'lucide-react';
import { Button, Notice } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { currentPosition } from '../../lib/location';
import {
  recordingSupported,
  startRecording,
  stopRecording,
  subscribeRecorder,
  type RecorderState,
} from '../../lib/trip-recorder';
import type { RideDto } from '../../lib/types';
import { whatsappLink } from '../landing/usePublicProfile';

const ACTIVE = ['CONFIRMED', 'DRIVER_ON_THE_WAY', 'DRIVER_ARRIVED', 'IN_PROGRESS'];

interface SosResult {
  emergencyNumber: string;
  shareUrl: string | null;
  location: { lat: number; lng: number } | null;
  vehicle: string;
  driver: string;
  contacts: Array<{ name: string; phone: string }>;
}

export function useRecorder() {
  const [s, setS] = useState<RecorderState | null>(null);
  useEffect(() => subscribeRecorder(setS), []);
  return s;
}

const mapsLink = (p: { lat: number; lng: number }) =>
  `https://www.google.com/maps?q=${p.lat.toFixed(6)},${p.lng.toFixed(6)}`;

/** Botón de emergencia: avisa (administración y contactos) y llama al 911. */
export async function triggerSos(rideId: string): Promise<SosResult> {
  const pos = await currentPosition().catch(() => null);
  return api<SosResult>(`/rides/${rideId}/sos`, {
    body: pos ? { lat: pos.lat, lng: pos.lng } : {},
  });
}

function ContactLinks({
  contacts,
  text,
}: {
  contacts: Array<{ name: string; phone: string }>;
  text: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      {contacts.map((c) => {
        const wa = whatsappLink(c.phone, text);
        return (
          <a
            key={c.phone}
            href={wa ?? '#'}
            target="_blank"
            rel="noreferrer"
            className="tap flex items-center gap-3 rounded-2xl border border-[var(--border)] px-4 h-12"
          >
            <Users className="size-4 text-[var(--text-muted)]" aria-hidden />
            <span className="flex-1 truncate">{c.name}</span>
            <span className="text-sm font-semibold text-[#128C7E]">WhatsApp</span>
          </a>
        );
      })}
    </div>
  );
}

/**
 * Centro de seguridad del viaje (escudo, como Uber): emergencia 911, compartir con contactos de confianza,
 * grabar audio del viaje y reportar un problema.
 */
export function SafetyCenter({
  ride,
  role,
  className,
}: {
  ride: RideDto;
  role: 'customer' | 'driver';
  className?: string;
}) {
  const toast = useToast();
  const rec = useRecorder();
  const [open, setOpen] = useState(false);
  const [sos, setSos] = useState<SosResult | null>(null);
  const [shared, setShared] = useState<{
    url: string;
    emailed: number;
    contacts: Array<{ name: string; phone: string }>;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const active = ACTIVE.includes(ride.status);
  const recordingThis = rec?.recording && rec.rideId === ride.id;

  const call911 = async () => {
    setBusy('sos');
    // La llamada sale de inmediato; el aviso al negocio y a los contactos se envía en paralelo.
    const req = triggerSos(ride.id);
    window.location.href = 'tel:911';
    try {
      setSos(await req);
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const share = async () => {
    setBusy('share');
    try {
      setShared(await api(`/rides/${ride.id}/share-contacts`, { method: 'POST' }));
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(null);
    }
  };

  const toggleRecording = async () => {
    if (recordingThis) {
      stopRecording();
      toast('Grabación guardada en este teléfono. Puedes adjuntarla a un reporte.', 'success');
    } else await startRecording(ride.id);
  };

  const base = role === 'customer' ? '/app' : '/driver';
  const v = ride.vehicle;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`tap size-11 rounded-full shadow-[var(--shadow-float)] flex items-center justify-center ${recordingThis ? 'bg-brand-red text-white' : 'bg-[var(--surface)] text-[#2563eb]'} ${className ?? ''}`}
        aria-label="Seguridad"
      >
        <Shield className="size-5" />
      </button>
      {open &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="safety-title"
            className="fixed inset-0 z-[3000] bg-black/50 flex items-end sm:items-center justify-center"
            onClick={() => setOpen(false)}
          >
            <div
              className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-[var(--surface)] p-5 pb-[max(env(safe-area-inset-bottom),1.25rem)] flex flex-col gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h2 id="safety-title" className="text-xl font-bold flex items-center gap-2">
                  <Shield className="size-6 text-[#2563eb]" aria-hidden /> Seguridad
                </h2>
                <button
                  className="tap size-9 rounded-full flex items-center justify-center"
                  onClick={() => setOpen(false)}
                  aria-label="Cerrar"
                >
                  <X className="size-5" />
                </button>
              </div>

              {sos ? (
                <div className="flex flex-col gap-3">
                  <Notice tone="danger">
                    Avisamos a la administración
                    {sos.contacts.length ? ' y a tus contactos de confianza (por correo)' : ''}. Si
                    la llamada no salió, toca el botón de nuevo.
                  </Notice>
                  <a
                    href="tel:911"
                    className="tap h-14 rounded-2xl bg-brand-red text-white font-bold flex items-center justify-center gap-2"
                  >
                    <Phone className="size-5" aria-hidden /> Llamar al 911
                  </a>
                  <div className="rounded-2xl bg-[var(--surface-2)] p-4 text-sm flex flex-col gap-1">
                    <p className="font-semibold">Datos para el 911</p>
                    {sos.location && (
                      <p>
                        Ubicación:{' '}
                        <a
                          className="underline"
                          href={mapsLink(sos.location)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {sos.location.lat.toFixed(5)}, {sos.location.lng.toFixed(5)}
                        </a>
                      </p>
                    )}
                    <p>Vehículo: {sos.vehicle}</p>
                    <p>Conductor: {sos.driver}</p>
                  </div>
                  {sos.contacts.length > 0 && (
                    <>
                      <p className="text-sm font-semibold">Escríbeles por WhatsApp</p>
                      <ContactLinks
                        contacts={sos.contacts}
                        text={`🚨 Estoy en una emergencia durante un viaje. Vehículo: ${sos.vehicle}.${sos.location ? ` Mi ubicación: ${mapsLink(sos.location)}` : ''}${sos.shareUrl ? ` Sigue el viaje: ${sos.shareUrl}` : ''}`}
                      />
                    </>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy === 'sos'}
                  onClick={() => void call911()}
                  className="tap rounded-2xl bg-brand-red text-white p-4 flex items-center gap-3 text-left disabled:opacity-70"
                >
                  <ShieldAlert className="size-8 shrink-0" aria-hidden />
                  <span>
                    <span className="block text-lg font-bold">Asistencia de emergencia</span>
                    <span className="block text-sm text-white/85">
                      Llama al 911 y avisa a la administración y a tus contactos con tu ubicación.
                    </span>
                  </span>
                </button>
              )}

              {v && (
                <div className="rounded-2xl border border-[var(--border)] p-4 text-sm">
                  <p className="text-[var(--text-muted)]">Comprueba antes de subir</p>
                  <p className="font-semibold">
                    {v.make} {v.model} · {v.color} · <span className="font-mono">{v.plate}</span>
                  </p>
                  {role === 'customer' && ride.pin?.code && (
                    <p className="mt-1">
                      Tu PIN para el conductor:{' '}
                      <strong className="font-mono text-lg tracking-widest">{ride.pin.code}</strong>
                    </p>
                  )}
                </div>
              )}

              {active && (
                <div className="flex flex-col gap-2">
                  <Button
                    variant="secondary"
                    block
                    icon={<Share2 className="size-5" />}
                    loading={busy === 'share'}
                    onClick={share}
                  >
                    Compartir viaje con mis contactos
                  </Button>
                  {shared &&
                    (shared.contacts.length ? (
                      <>
                        {shared.emailed > 0 && (
                          <p className="text-xs text-[var(--text-muted)]">
                            Enviado por correo a {shared.emailed} contacto
                            {shared.emailed > 1 ? 's' : ''}.
                          </p>
                        )}
                        <ContactLinks
                          contacts={shared.contacts}
                          text={`Sigue mi viaje en vivo con TuConductorRD: ${shared.url}`}
                        />
                      </>
                    ) : (
                      <Notice>
                        Aún no tienes contactos de confianza.{' '}
                        <Link
                          to={`${base}/${role === 'customer' ? 'profile' : 'more'}#seguridad`}
                          className="underline font-semibold"
                          onClick={() => setOpen(false)}
                        >
                          Agregar contactos
                        </Link>
                      </Notice>
                    ))}
                </div>
              )}

              {active && recordingSupported() && (
                <div className="flex flex-col gap-1">
                  <Button
                    variant={recordingThis ? 'accent' : 'secondary'}
                    block
                    icon={
                      recordingThis ? <MicOff className="size-5" /> : <Mic className="size-5" />
                    }
                    onClick={() => void toggleRecording()}
                  >
                    {recordingThis ? 'Detener grabación' : 'Grabar audio del viaje'}
                  </Button>
                  <p className="text-xs text-[var(--text-muted)]">
                    El audio queda solo en tu teléfono; nadie lo escucha a menos que lo adjuntes a
                    un reporte. Se borra a los 7 días.
                  </p>
                  {rec?.error && <p className="text-xs text-brand-red">{rec.error}</p>}
                </div>
              )}

              <Link to={`${base}/rides/${ride.id}?report=1`} onClick={() => setOpen(false)}>
                <Button variant="ghost" block icon={<TriangleAlert className="size-5" />}>
                  Reportar un problema de seguridad
                </Button>
              </Link>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
